import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '../lib/api';
import type { Category, Product } from '../lib/api';
import { money } from '../lib/i18n';
import { useSeo } from '../lib/head';
import { Link } from '../lib/router';
import { homeMeta, privateMeta } from '../lib/seo';
import { useSettings } from '../lib/settings';
import { useStore } from '../lib/store';

/**
 * Условия заказа на первом экране. Человек должен узнать про минимальную
 * сумму до того, как наберёт корзину, а не в момент оформления.
 */
function Terms() {
  const { lang, currency, t } = useStore();
  const settings = useSettings();
  if (!settings) return null;

  const min = settings.minOrder[currency];
  if (min === null || min === undefined) return null;

  // В драмах заказывают по Армении, в долларах — со всего мира. Цену доставки
  // здесь не называем: она зависит от адреса, поэтому просто «+ доставка»
  const amount = money(min, currency, lang);
  return (
    <span className="tape tape--plain">
      {currency === 'AMD' ? t.minOrderArmenia(amount) : t.minOrderWorld(amount)}
    </span>
  );
}

/**
 * Галочка «скрыть то, чего нет в наличии». Выбор запоминается в браузере,
 * по умолчанию закончившиеся чаи видны
 */
const HIDE_KEY = 'vishap.hideOutOfStock';

function readHide(): boolean {
  try {
    return localStorage.getItem(HIDE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeHide(hide: boolean): void {
  try {
    if (hide) localStorage.setItem(HIDE_KEY, '1');
    else localStorage.removeItem(HIDE_KEY);
  } catch {
    // Без хранилища галочка просто не переживёт перезагрузку
  }
}

export function Catalog() {
  const { lang, currency, t } = useStore();
  useSeo(homeMeta(lang));
  const [params, setParams] = useSearchParams();
  const active = params.get('category') ?? '';

  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [hideOut, setHideOut] = useState(readHide);

  const toggleHide = (hide: boolean) => {
    setHideOut(hide);
    writeHide(hide);
  };

  // Фильтр на месте: список уже пришёл целиком, лишний запрос не нужен
  const shown = products && hideOut ? products.filter((p) => p.inStock) : products;

  useEffect(() => {
    api
      .categories(lang)
      .then((r) => setCategories(r.items))
      .catch(() => setCategories([]));
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    setProducts(null);
    setFailed(false);

    api
      .products(lang, currency, active ? { category: active } : {})
      .then((r) => {
        if (!cancelled) setProducts(r.items);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [lang, currency, active]);

  const pick = (slug: string) => {
    if (slug) params.set('category', slug);
    else params.delete('category');
    setParams(params, { replace: true });
  };

  return (
    <>
      <section className="wrap hero">
        <span className="hand">{t.tagline}</span>
        <h1 className="hook">{t.heroTitle}</h1>
        <p className="lede">{t.heroLede}</p>
        <Terms />
      </section>

      <section className="wrap page" style={{ paddingTop: 0 }}>
        <div className="chips">
          <button
            type="button"
            className="chip"
            aria-pressed={active === ''}
            onClick={() => pick('')}
          >
            {t.all}
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              type="button"
              className="chip"
              aria-pressed={active === c.slug}
              onClick={() => pick(c.slug)}
            >
              {c.name}
              <small>{c.productsCount}</small>
            </button>
          ))}
          <label className="stock-toggle">
            <input
              type="checkbox"
              role="switch"
              checked={hideOut}
              onChange={(e) => toggleHide(e.target.checked)}
            />
            {t.hideOutOfStock}
          </label>
        </div>

        {failed && (
          <div className="notice notice--warn">
            {t.error}.{' '}
            <button type="button" className="btn btn--ghost btn--small" onClick={() => pick(active)}>
              {t.retry}
            </button>
          </div>
        )}

        {products === null && !failed && (
          <div className="grid">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="skeleton" />
            ))}
          </div>
        )}

        {shown?.length === 0 && (
          <div className="empty">
            <p>{products && products.length > 0 ? t.allOutOfStock : t.empty}</p>
            {products && products.length > 0 && (
              <button type="button" className="btn btn--ghost btn--small" onClick={() => toggleHide(false)}>
                {t.showOutOfStock}
              </button>
            )}
          </div>
        )}

        {shown && shown.length > 0 && (
          <div className="grid">
            {shown.map((p) => (
              <Link key={p.id} to={`/tea/${p.slug}`} className="card">
                <div className="card__media">
                  {p.image ? (
                    <img src={p.image} alt={p.title} loading="lazy" />
                  ) : (
                    <span className="card__placeholder">{t.photo}</span>
                  )}
                </div>
                <div className="card__body">
                  {p.category && <span className="card__cat">{p.category.name}</span>}
                  <h2 className="card__title">{p.title}</h2>
                  {p.short && <p className="muted" style={{ margin: 0 }}>{p.short}</p>}
                  <div className="card__price">
                    {p.inStock ? (
                      p.priceFrom !== null && (
                        <>
                          <small>{t.from} </small>
                          {money(p.priceFrom, currency, lang)}
                        </>
                      )
                    ) : (
                      <span className="muted">{t.outOfStock}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

/**
 * Незнакомый адрес. Показываем каталог, чтобы человек не упёрся в пустую
 * страницу, но поисковику говорим, что индексировать здесь нечего
 */
export function NotFound() {
  const { lang } = useStore();
  useSeo(privateMeta(lang, 'notFoundTitle'));
  return <Catalog />;
}
